import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { dynamicAdapterConfigsApi } from 'api';
import { QueryKey } from 'enums';
import { useMutation, usePartialQuery } from 'hooks';
import {
  ConfigValidationResult,
  CreateDynamicAdapterConfigDto,
  DynamicAdapterConfig,
  DynamicPayinConfig,
  DynamicPayoutConfig,
} from 'types';

export const useDynamicAdapterConfigs = () => {
  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(
    QueryKey.DynamicAdapterConfigs,
    dynamicAdapterConfigsApi.getAllPaginated,
  );

  const invalidateQuery = () => {
    queryClient.invalidateQueries(QueryKey.DynamicAdapterConfigs);
  };

  const createMutation = useMutation<
    DynamicAdapterConfig,
    AxiosError,
    CreateDynamicAdapterConfigDto
  >(dynamicAdapterConfigsApi.create, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const updateMutation = useMutation<
    DynamicAdapterConfig,
    AxiosError,
    { id: string; data: Partial<CreateDynamicAdapterConfigDto> }
  >(dynamicAdapterConfigsApi.update, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const removeMutation = useMutation<void, AxiosError, string>(
    dynamicAdapterConfigsApi.remove,
    {
      notifierType: 'remove',
      onSuccess: invalidateQuery,
    },
  );

  const activateMutation = useMutation<
    DynamicAdapterConfig,
    AxiosError,
    string
  >(dynamicAdapterConfigsApi.activate, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const deactivateMutation = useMutation<
    DynamicAdapterConfig,
    AxiosError,
    string
  >(dynamicAdapterConfigsApi.deactivate, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const validateMutation = useMutation<
    ConfigValidationResult,
    AxiosError,
    string
  >(dynamicAdapterConfigsApi.validate, {
    notifierType: 'none',
  });

  const validateJsonMutation = useMutation<
    ConfigValidationResult,
    AxiosError,
    { payinConfig?: DynamicPayinConfig; payoutConfig?: DynamicPayoutConfig }
  >(dynamicAdapterConfigsApi.validateJson, {
    notifierType: 'none',
  });

  const cloneMutation = useMutation<
    DynamicAdapterConfig,
    AxiosError,
    { id: string; name: string }
  >(({ id, name }) => dynamicAdapterConfigsApi.clone(id, name), {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  return {
    queryResult,
    createMutation,
    updateMutation,
    removeMutation,
    activateMutation,
    deactivateMutation,
    validateMutation,
    validateJsonMutation,
    cloneMutation,
    invalidateQuery,
  };
};

/**
 * Returns the active dynamic adapter config for a given provider type code,
 * or null if none exists. Used by the P2P provider create form to discover
 * `requiredCredentials` for a non-system (dynamic) provider type.
 */
export const useActiveDynamicAdapterConfig = (providerType?: string) => {
  const { data, isLoading } = useQuery<DynamicAdapterConfig[], AxiosError>({
    queryKey: [QueryKey.DynamicAdapterConfigs, 'all'],
    queryFn: dynamicAdapterConfigsApi.getAll,
  });

  const config = useMemo(() => {
    if (!providerType || !data) {
      return null;
    }
    return (
      data.find(
        (item) => item.providerType === providerType && item.isActive,
      ) || null
    );
  }, [data, providerType]);

  return { config, isLoading };
};
